import { cancelIfLate, createFileRequest, createJSONRequest, createPagedRequest } from './create-request'
import querify from '../util/querify'

// Fetch data for knowledge base
// Accepted parameters: tags & query
export const fetchKnowledgeBaseData = cancelIfLate((parameters) => createPagedRequest({
  method: 'GET',
  url: `/api/dashboards/knowledge?${querify(parameters)}`,
}))

// Fetch single knowledge base item by elasticId
export const fetchKnowledgeBaseItem = cancelIfLate((sqlId) => createJSONRequest({
  method: 'GET',
  url: `/api/knowledge-base/posts/${sqlId}`,
}))

export const buildExportContentLink = (parameters) => {
  return `/api/dashboards/knowledge?${querify(parameters)}`
}

export const acceptContent = (fileId) => createJSONRequest({
  url: `/api/files/${fileId}/accept`,
  method: 'PUT',
})

export const denyContent = (fileId) => createJSONRequest({
  url: `/api/files/${fileId}/deny`,
  method: 'PUT',
})

export const contentFeedback = (fileId, { feedback, dictionary_id }) => createJSONRequest({
  url: `/api/files/${fileId}/feedback`,
  method: 'PUT',
  data: { feedback, dictionary_id },
})

export const annotateArticle = (id, conceptSearchId, bustCache = false) => createJSONRequest({
  method: 'GET',
  url: `/api/annotate/${id}?concept_search_id=${conceptSearchId}&bustCache=${bustCache}`,
})

export const trackArticle = (fileId, conceptSearchId, type) => createJSONRequest({
  method: 'PUT',
  url: `/api/files/${fileId}/track`,
  data: { conceptSearchId, type },
})

export const likeArticle = (fileId, status) => createJSONRequest({
  method: 'PUT',
  url: `/api/files/${fileId}/like`,
  data: { status },
})

export const removeArticle = (fileId, conceptSearchId) => createJSONRequest({
  method: 'PUT',
  url: `/api/files/${fileId}/remove-from-search`,
  data: { conceptSearchId },
})

// ===== KNOWLEDGE BASE CATEGORIES =====
// Categories are ecosystem-wide, not community-specific

export const fetchCategories = cancelIfLate(() => createJSONRequest({
  method: 'GET',
  url: '/api/knowledge-base/categories',
}))

export const createCategory = (categoryData) => createJSONRequest({
  method: 'POST',
  url: '/api/knowledge-base/categories',
  data: categoryData,
})

export const updateCategory = (categoryId, categoryData) => createJSONRequest({
  method: 'PUT',
  url: `/api/knowledge-base/categories/${categoryId}`,
  data: categoryData,
})

export const deleteCategory = (categoryId) => createJSONRequest({
  method: 'DELETE',
  url: `/api/knowledge-base/categories/${categoryId}`,
})

export const reorderCategories = (categoryIds) => createJSONRequest({
  method: 'POST',
  url: '/api/knowledge-base/categories/reorder',
  data: { order: categoryIds },
})

// ===== KNOWLEDGE BASE POSTS =====
export const fetchPost = cancelIfLate((postId, communityId = null) => createJSONRequest({
  method: 'GET',
  url: `/api/knowledge-base/posts/${postId}`,
}))

export const createPost = (postData) => {
  // Check if postData is FormData (for file uploads)
  if (postData instanceof FormData) {
    return createFileRequest({
      method: 'POST',
      url: '/api/knowledge-base/posts',
      body: postData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  // Regular JSON request for simple data
  return createJSONRequest({
    method: 'POST',
    url: baseUrl,
    data: postData,
  })
}

export const updatePost = (postId, postData) => {
  // Check if postData is FormData (for file uploads)
  if (postData instanceof FormData) {
    // Add _method field for Laravel to handle PUT with FormData
    postData.append('_method', 'PUT')

    return createFileRequest({
      method: 'POST', // Use POST with _method for FormData compatibility
      url: `/api/knowledge-base/posts/${postId}`,
      body: postData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  // Regular JSON request for simple data
  return createJSONRequest({
    method: 'PUT',
    url: baseUrl,
    data: postData,
  })
}

/* export const deletePost = (postId, communityId = null) => createJSONRequest({
  method: 'DELETE',
  url: communityId ? `/api/communities/${communityId}/knowledge-base/posts/${postId}` : `/api/knowledge-base/posts/${postId}`,
})

export const fetchPostsByCategory = cancelIfLate((parameters) => createPagedRequest({
  method: 'GET',
  url: parameters.communityId ? `/api/communities/${parameters.communityId}/knowledge-base/categories/${parameters.category_id}/posts?${querify(parameters)}` : `/api/knowledge-base/categories/${parameters.category_id}/posts?${querify(parameters)}`,
}))

export const fetchUncategorizedPosts = cancelIfLate((parameters = {}) => createPagedRequest({
  method: 'GET',
  url: parameters.communityId ? `/api/communities/${parameters.communityId}/knowledge-base/posts/uncategorized?${querify(parameters)}` : `/api/knowledge-base/posts/uncategorized?${querify(parameters)}`,
}))

export const searchPosts = cancelIfLate((parameters = {}) => createPagedRequest({
  method: 'GET',
  url: `/api/knowledge-base/posts/search?${querify(parameters)}`,
})) */

// ===== KNOWLEDGE BASE BOOKMARKS =====

/* export const fetchBookmarks = cancelIfLate((parameters = {}) => createPagedRequest({
  method: 'GET',
  url: parameters.communityId ? `/api/communities/${parameters.communityId}/knowledge-base/bookmarks?${querify(parameters)}` : `/api/knowledge-base/bookmarks?${querify(parameters)}`,
})) */

export const toggleBookmark = (postId) => createJSONRequest({
  method: 'POST',
  url: `/api/knowledge-base/posts/${postId}/bookmark`,
})

export const checkBookmark = cancelIfLate((postId, communityId = null) => createJSONRequest({
  method: 'GET',
  url: `/api/knowledge-base/posts/${postId}/bookmark/check`,
}))

/* export const removeBookmark = (bookmarkId, communityId = null) => createJSONRequest({
  method: 'DELETE',
  url: communityId ? `/api/communities/${communityId}/knowledge-base/bookmarks/${bookmarkId}` : `/api/knowledge-base/bookmarks/${bookmarkId}`,
}) */

// Fetch posts by creator/user
export const fetchPostsByUser = cancelIfLate((userId, parameters = {}) => createPagedRequest({
  method: 'GET',
  url: `/api/knowledge-base/creators/${userId}/posts?${querify(parameters)}`,
}))

// ===== KNOWLEDGE BASE CONTRIBUTORS =====

export const fetchContributors = (postId) => createJSONRequest({
  method: 'GET',
  url: `/api/knowledge-base/posts/${postId}/contributors`,
})

export const addContributor = (postId, userId) => createJSONRequest({
  method: 'POST',
  url: `/api/knowledge-base/posts/${postId}/contributors`,
  data: { user_id: userId },
})

export const removeContributor = (postId, userId) => createJSONRequest({
  method: 'DELETE',
  url: `/api/knowledge-base/posts/${postId}/contributors/${userId}`,
})

// ===== KNOWLEDGE BASE EDITING PRESENCE =====

export const getEditors = (postId) => createJSONRequest({
  method: 'GET',
  url: `/api/knowledge-base/posts/${postId}/editing`,
})

export const startEditing = (postId) => createJSONRequest({
  method: 'POST',
  url: `/api/knowledge-base/posts/${postId}/editing`,
})

export const stopEditing = (postId) => createJSONRequest({
  method: 'DELETE',
  url: `/api/knowledge-base/posts/${postId}/editing`,
})

// ── Post Lock (exclusive edit lock) ──────────────────────────────────────────

export const getLockStatus = (postId) => createJSONRequest({
  method: 'GET',
  url: `/api/knowledge-base/posts/${postId}/lock`,
})

export const acquireLock = (postId) => createJSONRequest({
  method: 'POST',
  url: `/api/knowledge-base/posts/${postId}/lock`,
})

export const refreshLock = (postId, token) => createJSONRequest({
  method: 'PATCH',
  url: `/api/knowledge-base/posts/${postId}/lock`,
  data: { token },
})

export const releaseLock = (postId, token) => createJSONRequest({
  method: 'DELETE',
  url: `/api/knowledge-base/posts/${postId}/lock`,
  data: { token },
})
